import type { MouseEvent as ReactMouseEvent } from "react";
import { FormEvent, useEffect, useState } from "react";
import { useAuth } from "../features/auth/useAuth";
import { messageFromUnknownHttpError } from "../lib/axiosError";
import "./Login.css";

function PeopleIcon() {
  return (
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path
        d="M24 22c4.4 0 8-3.6 8-8s-3.6-8-8-8-8 3.6-8 8 3.6 8 8 8z"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 40c0-7.7 6.3-14 14-14s14 6.3 14 14"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M34 18c2.2 0 4-1.8 4-4s-1.8-4-4-4"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M38 40v-1c0-3.3-2.7-6-6-6h-2"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const Login = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [forgotOpen, setForgotOpen] = useState(false);

  useEffect(() => {
    if (!forgotOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setForgotOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [forgotOpen]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage(null);
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setErrorMessage("Enter your work email.");
      return;
    }
    if (!password) {
      setErrorMessage("Enter your password.");
      return;
    }
    try {
      setIsSubmitting(true);
      await login({ email: trimmedEmail, password });
    } catch (err) {
      setErrorMessage(messageFromUnknownHttpError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldError = Boolean(errorMessage);

  const closeForgotBackdrop = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) setForgotOpen(false);
  };

  return (
    <div className="login-page">
      <div className="login-page-bg" aria-hidden />
      <div className="login-card">
        <div className="login-card-icon">
          <PeopleIcon />
        </div>
        <h1>Provisioning Portal</h1>
        <p className="login-card-sub">
          Sign in to manage uploads, review requests, and monitor provisioning activity.
        </p>

        <form className="login-form" onSubmit={handleSubmit} noValidate>
          {errorMessage ? (
            <div className="login-alert" role="alert" aria-live="polite">
              {errorMessage}
            </div>
          ) : null}

          <div>
            <label htmlFor="email" className="login-label">
              Work email
            </label>
            <div className={`login-field-wrap${fieldError ? " login-field-wrap--error" : ""}`}>
              <span className="login-field-icon" aria-hidden>
                ✉
              </span>
              <input
                id="email"
                type="email"
                value={email}
                autoComplete="username"
                placeholder="john.doe@example.com"
                onChange={(event) => {
                  setEmail(event.target.value);
                  if (errorMessage) setErrorMessage(null);
                }}
              />
            </div>
          </div>

          <div>
            <div className="login-label-row">
              <label htmlFor="password" className="login-label" style={{ marginBottom: 0 }}>
                Password
              </label>
              <button type="button" className="login-forgot" onClick={() => setForgotOpen(true)}>
                Forgot password?
              </button>
            </div>
            <div className={`login-field-wrap${fieldError ? " login-field-wrap--error" : ""}`}>
              <span className="login-field-icon" aria-hidden>
                🔒
              </span>
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                autoComplete="current-password"
                placeholder="••••••••"
                onChange={(event) => {
                  setPassword(event.target.value);
                  if (errorMessage) setErrorMessage(null);
                }}
              />
              <button
                type="button"
                className="login-toggle-visibility"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                tabIndex={-1}
              >
                <span style={{ fontSize: "0.72rem", fontWeight: 700 }}>{showPassword ? "Hide" : "Show"}</span>
              </button>
            </div>
          </div>

          <button type="submit" className="login-submit" disabled={isSubmitting}>
            {isSubmitting ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>

      {forgotOpen ? (
        <div
          className="login-modal-overlay"
          role="presentation"
          onMouseDown={closeForgotBackdrop}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="login-forgot-title"
            className="login-modal"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h2 id="login-forgot-title">Forgot password?</h2>
            <p>
              Reach out to the <strong>super admin</strong> who gave you your account credentials. They can generate new
              login details for you.
            </p>
            <div className="login-modal-actions">
              <button type="button" className="login-modal-close" onClick={() => setForgotOpen(false)}>
                Got it
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Login;
